/* eslint-disable react-func/max-lines-per-function */
import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import {
  fetchCampaignsAction,
  fetchCurrentCampaignAction,
  fetchNotificationEventsAction,
  fetchStatsAction,
  resendPendingMessagesAction,
} from './messages.async-actions';
import {
  addNewCampaignReducer,
  setCampaignsReducer,
  setCurrentCampaignReducer,
  setNotificationsReducer,
  setStatsReducer,
  updateDateRangeReducer,
} from './messages.reducers';
import { MessagesState } from './messages.types';

import { asyncThunkStatus } from '@/constants/common';
import { defaultStats } from '@/services/technical-solutions-api/constants';
import { dayjsToString } from '@/utils/date';

export const initialState: MessagesState = {
  dateRange: [dayjsToString(dayjs().date(1)), dayjsToString()],
  campaigns: [],
  notifications: [],
  stats: defaultStats,
  currentCampaign: null,
  fetchCampaignsStatus: asyncThunkStatus.idle,
  fetchNotificationEventsStatus: asyncThunkStatus.idle,
  resendPendingMessages: asyncThunkStatus.idle,
  fetchCurrentCampaign: asyncThunkStatus.idle,
  fetchStatsStatus: asyncThunkStatus.idle,
};

export const messagesSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    setCampaigns: setCampaignsReducer,
    setStats: setStatsReducer,
    setNotifications: setNotificationsReducer,
    setCurrentCampaign: setCurrentCampaignReducer,
    updateDateRange: updateDateRangeReducer,
    addNewCampaign: addNewCampaignReducer,
  },
  extraReducers(builder) {
    builder.addCase(fetchCampaignsAction.pending, (state: MessagesState) => {
      state.fetchCampaignsStatus = asyncThunkStatus.pending;
    });
    builder.addCase(fetchCampaignsAction.fulfilled, (state: MessagesState) => {
      state.fetchCampaignsStatus = asyncThunkStatus.succeeded;
    });
    builder.addCase(fetchCampaignsAction.rejected, (state: MessagesState) => {
      state.fetchCampaignsStatus = asyncThunkStatus.failed;
    });

    builder.addCase(fetchStatsAction.pending, (state: MessagesState) => {
      state.fetchStatsStatus = asyncThunkStatus.pending;
    });
    builder.addCase(fetchStatsAction.fulfilled, (state: MessagesState) => {
      state.fetchStatsStatus = asyncThunkStatus.succeeded;
    });
    builder.addCase(fetchStatsAction.rejected, (state: MessagesState) => {
      state.fetchStatsStatus = asyncThunkStatus.failed;
    });

    builder.addCase(
      fetchNotificationEventsAction.pending,
      (state: MessagesState) => {
        state.fetchNotificationEventsStatus = asyncThunkStatus.pending;
      }
    );
    builder.addCase(
      fetchNotificationEventsAction.fulfilled,
      (state: MessagesState) => {
        state.fetchNotificationEventsStatus = asyncThunkStatus.succeeded;
      }
    );
    builder.addCase(
      fetchNotificationEventsAction.rejected,
      (state: MessagesState) => {
        state.fetchNotificationEventsStatus = asyncThunkStatus.failed;
      }
    );
    builder.addCase(
      fetchCurrentCampaignAction.pending,
      (state: MessagesState) => {
        state.fetchCurrentCampaign = asyncThunkStatus.pending;
      }
    );
    builder.addCase(
      fetchCurrentCampaignAction.fulfilled,
      (state: MessagesState) => {
        state.fetchCurrentCampaign = asyncThunkStatus.succeeded;
      }
    );
    builder.addCase(
      fetchCurrentCampaignAction.rejected,
      (state: MessagesState) => {
        state.fetchCurrentCampaign = asyncThunkStatus.failed;
      }
    );
    builder.addCase(
      resendPendingMessagesAction.pending,
      (state: MessagesState) => {
        state.resendPendingMessages = asyncThunkStatus.pending;
      }
    );
    builder.addCase(
      resendPendingMessagesAction.fulfilled,
      (state: MessagesState) => {
        state.resendPendingMessages = asyncThunkStatus.succeeded;
      }
    );
    builder.addCase(
      resendPendingMessagesAction.rejected,
      (state: MessagesState) => {
        state.resendPendingMessages = asyncThunkStatus.failed;
      }
    );
  },
});

export const {
  updateDateRange,
  setCampaigns,
  setStats,
  setNotifications,
  setCurrentCampaign,
  addNewCampaign,
} = messagesSlice.actions;

export default messagesSlice.reducer;
